// Floating AI Legal Assistant & Enterprise Live Support (LexAI).
// Original Dark Premium FAANG Standard with 100% Persistent Chat Continuity.
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  createSupportTicketApi,
  replySupportTicketApi,
} from "../api/supportService";
import "../css/ChatWidget.css";

const CUSTOMER_SUGGESTIONS = [
  "🚨 24/7 Urgent Bail Help",
  "⚖️ Connect with Advocate",
  "🏢 Property & RERA Dispute",
  "💼 Business & Company Law",
  "💳 Wallet & Refund Support",
  "📝 Draft Legal Notice",
];

const ADVOCATE_SUGGESTIONS = [
  "💳 Payout & 10% TDS Calculation",
  "📄 Bar Council Verification Status",
  "📅 e-Courts Cause List Auto-Sync",
  "📁 Case Docket Filing",
  "🎧 Helpdesk Support",
];

const LEGAL_KNOWLEDGE_BASE = [
  {
    keywords: [
      "criminal",
      "fir",
      "arrest",
      "bail",
      "police",
      "jail",
      "police station",
      "custody",
    ],
    reply:
      "Under the Bharatiya Nyaya Sanhita (BNS) & BNSS, anticipatory bail (Sec 482 BNSS) and regular bail applications must be filed promptly with the Sessions Court or High Court. Verified criminal advocates are available 24/7 for urgent lock-in.",
    action: { label: "🚨 Open 24/7 Urgent Broadcast", type: "urgent" },
  },
  {
    keywords: [
      "property",
      "land",
      "flat",
      "rera",
      "rent",
      "tenant",
      "landlord",
      "possession",
      "builder",
    ],
    reply:
      "Property disputes, delayed possession claims (Sec 18 RERA), and registry partition suits are handled by our top-rated real estate advocates. Free 10-minute discovery chat available.",
    action: {
      label: "Browse Property Advocates",
      type: "lawyers",
      query: "Property",
    },
  },
  {
    keywords: [
      "divorce",
      "marriage",
      "custody",
      "maintenance",
      "family",
      "498a",
      "alimony",
    ],
    reply:
      "Family disputes and mutual consent divorce (Sec 13B Hindu Marriage Act) are handled with 100% legal confidentiality. Our family advocates assist with court filings, mediation, and child custody.",
    action: {
      label: "Find Family Advocates",
      type: "lawyers",
      query: "Family",
    },
  },
  {
    keywords: [
      "company",
      "business",
      "startup",
      "contract",
      "corporate",
      "nclt",
      "merger",
      "shareholder",
      "trademark",
    ],
    reply:
      "For corporate incorporation, founder agreements, trademark protection, and NCLT IBC insolvency defense, our corporate legal team provides end-to-end advisory.",
    action: {
      label: "View Corporate Advocates",
      type: "lawyers",
      query: "Corporate",
    },
  },
  {
    keywords: [
      "cyber",
      "fraud",
      "freeze",
      "bank freeze",
      "usdt",
      "p2p",
      "phishing",
      "scam",
      "crypto",
    ],
    reply:
      "If your bank account is frozen under Sec 91/102 CrPC or IT Act notices due to P2P transactions, our cyber litigation advocates draft immediate representations to the Cyber Police Nodal Officer.",
    action: {
      label: "Consult Cyber Advocates",
      type: "lawyers",
      query: "Cyber",
    },
  },
  {
    keywords: ["cheque", "138", "bounce", "dishonour", "ni act"],
    reply:
      "Under Section 138 of the Negotiable Instruments Act, a statutory demand notice must be served within 30 days of memo receipt. Our advocates draft and issue legal notices within 24 hours.",
    action: {
      label: "Browse NI Act Advocates",
      type: "lawyers",
      query: "Banking",
    },
  },
  {
    keywords: [
      "payout",
      "tds",
      "gst",
      "194j",
      "earnings",
      "invoice",
      "commission",
    ],
    reply:
      "Consultation payouts are audited with 18% GST and 10% TDS withholding (Sec 194J). Verified advocates receive quarterly Form 16A TDS certificates directly in their Billing portal.",
    action: {
      label: "Open Payouts & Billing",
      type: "route",
      path: "/lawyer/billing",
    },
  },
  {
    keywords: [
      "verification",
      "bar council",
      "approval",
      "document",
      "enrollment",
    ],
    reply:
      "Advocate credentials undergo compliance verification within 2–4 hours. Make sure your Bar Council certificate and LLB degree are uploaded in your Profile Editor.",
    action: {
      label: "Open Profile Editor",
      type: "route",
      path: "/lawyer/profile-submit",
    },
  },
  {
    keywords: ["refund", "wallet", "recharge", "deduct", "money"],
    reply:
      "Wallet recharges are protected by instant refund guarantee. If a lawyer consultation didn't take place, your money is credited back automatically.",
    action: { label: "Manage Wallet", type: "route", path: "/wallet" },
  },
];

// Real-time IST Local Time Formatter
function getLocalTimeString() {
  return new Date().toLocaleTimeString("en-IN", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
}

function ChatWidget({ isOpen, onClose }) {
  const navigate = useNavigate();
  const { user } = useAuth();

  const role = String(user?.roleType || user?.role || "customer").toLowerCase();
  const isAdvocate =
    role === "agent" || role === "lawyer" || role === "advocate";
  const userKey =
    user?.userId ||
    user?.id ||
    user?.email ||
    (isAdvocate ? "agent_guest" : "customer_guest");
  const storageMessagesKey = `lexai_messages_${userKey}`;
  const storageStartedKey = `lexai_started_${userKey}`;

  const [started, setStarted] = useState(() => {
    return localStorage.getItem(storageStartedKey) === "true";
  });

  const [messages, setMessages] = useState(() => {
    try {
      const saved = localStorage.getItem(storageMessagesKey);
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  const [input, setInput] = useState("");
  const [typing, setTyping] = useState(false);
  const [minimised, setMinimised] = useState(false);
  const [activeTicketId, setActiveTicketId] = useState(() => {
    return (
      sessionStorage.getItem("lexai_active_ticket_id") ||
      localStorage.getItem(`lexai_ticket_${userKey}`) ||
      null
    );
  });
  const [isEscalated, setIsEscalated] = useState(() => {
    return (
      sessionStorage.getItem("lexai_is_escalated") === "true" ||
      localStorage.getItem(`lexai_esc_${userKey}`) === "true"
    );
  });
  const bottomRef = useRef(null);

  // Permanent Storage Sync
  useEffect(() => {
    if (messages.length > 0) {
      localStorage.setItem(storageMessagesKey, JSON.stringify(messages));
      localStorage.setItem(storageStartedKey, "true");
    }
  }, [messages, storageMessagesKey, storageStartedKey]);

  useEffect(() => {
    if (activeTicketId) {
      localStorage.setItem(`lexai_ticket_${userKey}`, activeTicketId);
      sessionStorage.setItem("lexai_active_ticket_id", activeTicketId);
    }
    if (isEscalated) {
      localStorage.setItem(`lexai_esc_${userKey}`, "true");
      sessionStorage.setItem("lexai_is_escalated", "true");
    }
  }, [activeTicketId, isEscalated, userKey]);

  useEffect(() => {
    if (isOpen && !minimised) {
      bottomRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, typing, isOpen, minimised]);

  // Real-time Support Reply Listener (In-Tab & Cross-Tab)
  useEffect(() => {
    const handleSupportReply = (replyData) => {
      if (!replyData || !replyData.reply) return;
      const { ticketId, reply, senderName } = replyData;

      if (ticketId) {
        setActiveTicketId(ticketId);
        setIsEscalated(true);
        sessionStorage.setItem("lexai_active_ticket_id", ticketId);
        sessionStorage.setItem("lexai_is_escalated", "true");
      }

      setMessages((prev) => {
        const uniqueKey = `reply-${ticketId}-${reply}`;
        if (prev.some((m) => m.uniqueKey === uniqueKey)) return prev;

        const updated = [
          ...prev,
          {
            id: Date.now() + Math.random(),
            from: "support",
            senderName: senderName || "Kanoon Talk Support Officer",
            uniqueKey,
            text: reply,
            ticketId: ticketId,
            time: getLocalTimeString(),
          },
        ];
        localStorage.setItem(storageMessagesKey, JSON.stringify(updated));
        localStorage.setItem(storageStartedKey, "true");
        return updated;
      });
    };

    const onCustomEvent = (e) => {
      if (e.detail) handleSupportReply(e.detail);
    };
    window.addEventListener("ticket-reply-dispatched", onCustomEvent);

    const onStorageChange = (e) => {
      if (e.key === "legalagent_latest_support_reply" && e.newValue) {
        try {
          const parsed = JSON.parse(e.newValue);
          handleSupportReply(parsed);
        } catch {}
      }
    };
    window.addEventListener("storage", onStorageChange);

    const interval = setInterval(() => {
      const storageKey = isAdvocate
        ? "legalagent_agent_tickets"
        : "legalagent_customer_tickets";
      try {
        const localTickets = JSON.parse(
          localStorage.getItem(storageKey) || "[]",
        );
        localTickets.forEach((t) => {
          if (t.messages && Array.isArray(t.messages)) {
            t.messages.forEach((m) => {
              if (m.sender === "support" || m.sender === "admin") {
                handleSupportReply({
                  ticketId: t.id,
                  reply: m.text,
                  senderName: m.senderName || "Kanoon Talk Support Officer",
                });
              }
            });
          }
        });
      } catch {}
    }, 2000);

    return () => {
      window.removeEventListener("ticket-reply-dispatched", onCustomEvent);
      window.removeEventListener("storage", onStorageChange);
      clearInterval(interval);
    };
  }, [isAdvocate, storageMessagesKey, storageStartedKey]);

  const handleStart = () => {
    setStarted(true);
    localStorage.setItem(storageStartedKey, "true");
    setTyping(true);
    setTimeout(() => {
      setTyping(false);
      const greeting = isAdvocate
        ? `Hello Adv. ${(user?.name || "Counsel").replace(/^Adv\.?\s+/i, "")}! I'm LexAI Advocate Intelligence. How can I assist you with your Payouts, Bar Verification, or platform queries today?`
        : `Hello! I'm LexAI, your 24/7 legal assistant. How can I help with your legal queries, emergency advocates, or platform support today?`;

      const initialMsg = [
        {
          id: 1,
          from: "bot",
          time: getLocalTimeString(),
          text: greeting,
        },
      ];
      setMessages(initialMsg);
      localStorage.setItem(storageMessagesKey, JSON.stringify(initialMsg));
    }, 400);
  };

  const handleClearHistory = () => {
    localStorage.removeItem(storageMessagesKey);
    localStorage.removeItem(storageStartedKey);
    localStorage.removeItem(`lexai_ticket_${userKey}`);
    localStorage.removeItem(`lexai_esc_${userKey}`);
    sessionStorage.removeItem("lexai_active_ticket_id");
    sessionStorage.removeItem("lexai_is_escalated");
    setStarted(false);
    setMessages([]);
    setActiveTicketId(null);
    setIsEscalated(false);
  };

  const ensureSupportSession = async (messageText) => {
    const isAdv = isAdvocate;
    const storageKey = isAdv
      ? "legalagent_agent_tickets"
      : "legalagent_customer_tickets";

    if (activeTicketId) {
      try {
        await replySupportTicketApi(activeTicketId, {
          senderRole: isAdv ? "advocate" : "client",
          senderName:
            user?.name || user?.fullName || (isAdv ? "Advocate" : "Client"),
          messageText: messageText,
        });
      } catch (err) {
        console.warn("Using offline thread append:", err);
      }

      const existing = JSON.parse(localStorage.getItem(storageKey) || "[]");
      const updated = existing.map((t) => {
        if (t.id === activeTicketId) {
          return {
            ...t,
            messages: [
              ...(t.messages || []),
              {
                sender: isAdv ? "advocate" : "client",
                text: messageText,
                time: getLocalTimeString(),
              },
            ],
          };
        }
        return t;
      });
      localStorage.setItem(storageKey, JSON.stringify(updated));
      window.dispatchEvent(new Event("ticket-created"));
      return activeTicketId;
    }

    const fallbackId = isAdv
      ? `AGNT-${Math.floor(100 + Math.random() * 900)}`
      : `CUST-${Math.floor(900 + Math.random() * 900)}`;
    const payload = {
      userId: user?.userId || user?.id || null,
      userRole: isAdv ? "Agent" : "Customer",
      email: user?.email || "",
      phone: user?.phone || user?.phoneNumber || "",
      city: user?.city || "",
      barCouncilNo: user?.enrollmentNo || user?.barCouncilNo || null,
      category: isAdv
        ? "Lawyer Platform Support"
        : "Customer Legal & Platform Support",
      subject: messageText.slice(0, 50) || "Live Customer Support Session",
      priority:
        messageText.toLowerCase().includes("urgent") ||
        messageText.toLowerCase().includes("bail")
          ? "Urgent"
          : "Normal",
      message: messageText,
    };

    let ticketCode = fallbackId;
    try {
      const apiRes = await createSupportTicketApi(payload);
      if (apiRes && apiRes.id) {
        ticketCode = apiRes.id;
      }
    } catch (err) {
      console.warn("Using fallback ticket code:", err);
    }

    const newTicket = {
      id: ticketCode,
      ...payload,
      advocateName: payload.name,
      status: "Open",
      date: new Date().toLocaleDateString("en-IN", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      }),
      assignedTo: "Lead Support Officer",
      messages: [
        {
          sender: isAdv ? "advocate" : "client",
          text: messageText,
          time: getLocalTimeString(),
        },
      ],
    };

    const existing = JSON.parse(localStorage.getItem(storageKey) || "[]");
    if (!existing.some((t) => t.id === ticketCode)) {
      existing.unshift(newTicket);
      localStorage.setItem(storageKey, JSON.stringify(existing));
    }

    setActiveTicketId(ticketCode);
    setIsEscalated(true);
    sessionStorage.setItem("lexai_active_ticket_id", ticketCode);
    sessionStorage.setItem("lexai_is_escalated", "true");
    window.dispatchEvent(new Event("ticket-created"));

    return ticketCode;
  };

  const processBotReply = async (text) => {
    const lower = text.toLowerCase();

    if (
      isEscalated ||
      lower.includes("agent") ||
      lower.includes("human") ||
      lower.includes("support") ||
      lower.includes("ticket") ||
      lower.includes("help me") ||
      lower.includes("connect")
    ) {
      const ticketId = await ensureSupportSession(text);
      if (!isEscalated) {
        return {
          reply: `Connecting you with our 24/7 Kanoon Talk Support Officers (Session #${ticketId}). A support officer has been notified and will reply right here in this chat shortly.`,
          isEscalation: true,
        };
      } else {
        return {
          reply: `Your message has been delivered to the active support session (#${ticketId}). Our support team will respond live here.`,
          isEscalation: true,
        };
      }
    }

    for (const item of LEGAL_KNOWLEDGE_BASE) {
      if (item.keywords.some((k) => lower.includes(k))) {
        return item;
      }
    }

    if (
      lower.includes("hello") ||
      lower.includes("hi") ||
      lower.includes("hey")
    ) {
      return {
        reply:
          "Hello! How can I assist you with your legal matters or advocate bookings today?",
      };
    }

    if (
      lower.includes("fee") ||
      lower.includes("cost") ||
      lower.includes("price") ||
      lower.includes("charge")
    ) {
      return {
        reply:
          "Advocate consultations start at ₹500/session with the first 10 minutes free on chat. You receive itemized GST invoices with full price transparency.",
        action: { label: "Browse Rates & Lawyers", type: "lawyers" },
      };
    }

    return {
      reply:
        "I understand. I can help guide you or connect you directly with a verified legal advocate or live support specialist.",
      action: {
        label: "👨‍⚖️ Connect with Support Officer",
        type: "escalate",
        query: text,
      },
    };
  };

  const sendMessage = async (text) => {
    const trimmed = text.trim();
    if (!trimmed || typing) return;

    const userMsg = {
      id: Date.now(),
      from: "user",
      text: trimmed,
      time: getLocalTimeString(),
    };
    setMessages((prev) => {
      const updated = [...prev, userMsg];
      localStorage.setItem(storageMessagesKey, JSON.stringify(updated));
      return updated;
    });
    setInput("");
    setTyping(true);

    const res = await processBotReply(trimmed);

    setTimeout(
      () => {
        setTyping(false);
        setMessages((prev) => {
          const updated = [
            ...prev,
            {
              id: Date.now() + 1,
              from: "bot",
              text: res.reply,
              action: res.action,
              time: getLocalTimeString(),
            },
          ];
          localStorage.setItem(storageMessagesKey, JSON.stringify(updated));
          return updated;
        });
      },
      400 + Math.random() * 200,
    );
  };

  const handleActionClick = async (action) => {
    if (!action) return;
    if (action.type === "urgent") {
      window.dispatchEvent(new Event("open-urgent-help-modal"));
    } else if (action.type === "lawyers") {
      navigate(
        action.query
          ? `/lawyers?area=${encodeURIComponent(action.query)}`
          : "/lawyers",
      );
      if (onClose) onClose();
    } else if (action.type === "route") {
      navigate(action.path);
      if (onClose) onClose();
    } else if (action.type === "escalate") {
      const ticketId = await ensureSupportSession(
        action.query || "Customer Assistance Request",
      );
      setMessages((prev) => {
        const updated = [
          ...prev,
          {
            id: Date.now(),
            from: "bot",
            text: `🟢 Live Support Session #${ticketId} activated. A support officer has been assigned and will reply here.`,
            time: getLocalTimeString(),
          },
        ];
        localStorage.setItem(storageMessagesKey, JSON.stringify(updated));
        return updated;
      });
    }
  };

  const handleKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage(input);
    }
  };

  const suggestions = isAdvocate ? ADVOCATE_SUGGESTIONS : CUSTOMER_SUGGESTIONS;

  return (
    <div
      className={`cw ${isOpen ? "cw--open" : ""} ${minimised ? "cw--min" : ""}`}
      aria-hidden={!isOpen}
    >
      {/* Header */}
      <div className="cw__header">
        <div className="cw__header-left">
          <div className="cw__logo">⚖️</div>
          <div>
            <div className="cw__title">LawyerGuide Assistant</div>
            <div className="cw__sub">
              {isEscalated
                ? `🟢 Live Support Active · #${activeTicketId}`
                : "24/7 Legal Support Desk · Online"}
            </div>
          </div>
        </div>
        <div className="cw__header-btns">
          {started && (
            <button
              className="cw__hbtn"
              onClick={handleClearHistory}
              title="New Chat / Clear History"
            >
              🔄
            </button>
          )}
          <button
            className="cw__hbtn"
            onClick={() => setMinimised(!minimised)}
            title={minimised ? "Expand" : "Minimise"}
          >
            {minimised ? "+" : "−"}
          </button>
          <button className="cw__hbtn" onClick={onClose} title="Close">
            ×
          </button>
        </div>
      </div>

      {/* Body */}
      <div className="cw__body">
        {!started ? (
          <div className="cw__welcome">
            <div className="cw__welcome-card">
              <p>
                Welcome to <strong>Kanoon Talk</strong>.
              </p>
              <p>
                I'm <strong>LexAI</strong>, your 24/7 intelligent legal advisor
                and support desk. Ask legal questions or connect with our
                support officers instantly.
              </p>
              <p className="cw__welcome-card-footer">
                🔒 High Court &amp; Supreme Court verified advocates. Encrypted
                end-to-end.
              </p>
            </div>
            <button className="cw__start-btn" onClick={handleStart}>
              Start AI Consultation &rarr;
            </button>
          </div>
        ) : (
          <div className="cw__messages">
            {messages.map((m) => (
              <div key={m.id} className={`cw__msg cw__msg--${m.from}`}>
                {m.from !== "user" && (
                  <div className="cw__avatar">
                    {m.from === "support" ? "👨‍💼" : "⚖️"}
                  </div>
                )}
                <div className="cw__msg-wrap">
                  {m.from === "support" && (
                    <div className="cw__support-badge">
                      {m.senderName || "Support Officer"} (Live Agent)
                    </div>
                  )}
                  <div className="cw__bubble">
                    {m.text}
                    {m.action && (
                      <div>
                        <button
                          className="cw__action-btn"
                          onClick={() => handleActionClick(m.action)}
                        >
                          {m.action.label} &rarr;
                        </button>
                      </div>
                    )}
                  </div>
                  <span className="cw__time">{m.time}</span>
                </div>
              </div>
            ))}

            {typing && (
              <div className="cw__msg cw__msg--bot">
                <div className="cw__avatar">⚖️</div>
                <div className="cw__typing">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
            )}
            <div ref={bottomRef} />
          </div>
        )}
      </div>

      {/* Suggestions Row */}
      {started && (
        <div className="cw__chips-row">
          {suggestions.map((s, idx) => (
            <button
              key={idx}
              className="cw__chip-btn"
              onClick={() => sendMessage(s)}
            >
              {s}
            </button>
          ))}
        </div>
      )}

      {/* Input Bar */}
      <div className="cw__input-bar">
        <input
          type="text"
          className="cw__input"
          placeholder={
            started
              ? "Type your legal inquiry or 'support'..."
              : "Click 'Start AI Consultation' above..."
          }
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKey}
          disabled={!started}
        />
        <button
          className="cw__send"
          onClick={() => sendMessage(input)}
          disabled={!started || !input.trim()}
          aria-label="Send message"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
            <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
          </svg>
        </button>
      </div>

      <div className="cw__powered">
        Kanoon Talk Enterprise AI · Indian Law Verified (BNS / BNSS / RERA / IT
        Act)
      </div>
    </div>
  );
}

export default ChatWidget;
